<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { useRouter } from 'vue-router/composables'

  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import ExamAction from '@/components/course/course-panel-contents/ExamAction.vue'

  import useIcons from '@/composables/useIcons'
  import useCourse from '@/composables/useCourse'
  import useI18n from '@/composables/useI18n'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useSegment from '@/composables/useSegment'

  import UsersApi from '@/services/api/UsersApi'

  import { UserModule } from '@/store/modules/user'
  import { CourseModule } from '@/store/modules/course'

  import { DateTime } from 'luxon'
  import eventBus from '@/main'

  const { course } = useCourse()
  const { translateString } = useI18n()
  const { closeModal, identifyUserInSegmentWithExamDates, downloadItem, showExamAppointmentModal, formatDate } =
    useCommonMixin()
  const { examAppointmentRemoved } = useSegment()

  const router = useRouter()

  defineEmits(['remove-date'])

  const { blankCalendar } = useIcons()

  const tipsDownloading = ref(false)
  const tipsDownloaded = ref(false)

  const courseExamAppointment = computed(() => {
    return UserModule.examAppointments?.find((appointment) => appointment.courseId === course.value?.id)
  })

  const examDateActionText = computed(() => {
    if (courseExamAppointment.value) {
      if (tipsDownloaded.value) {
        return translateString('examDatePicker.tipsDownloaded')
      }
      return translateString('examDatePicker.tipsToPrepare')
    }
    return translateString('courses.examSimulation.specifyExamDate')
  })

  const daysLeftToExam = computed(() => {
    if (!courseExamAppointment.value) return 0

    const examDate = DateTime.fromISO(courseExamAppointment.value.date)
    const now = DateTime.now()
    const diff = examDate.diff(now, 'days')
    return Math.ceil(diff.days)
  })

  const examDateHasPassed = computed(() => {
    if (!courseExamAppointment.value) return false
    return daysLeftToExam.value < 0
  })

  const goToExam = () => {
    if (!course.value || !course.value.exam) return
    router.push({ path: `/courses/${course.value.id}/exam/${course.value.exam.id}/start` })
  }

  const handleExamDateActionClick = () => {
    if (courseExamAppointment.value) {
      tipsDownloading.value = true
      downloadExamTipsDocument()

      setTimeout(() => {
        tipsDownloading.value = false
        tipsDownloaded.value = true
      }, 250)
    } else {
      showExamAppointmentModal(course.value!.id)
    }
  }

  const downloadExamTipsDocument = () => {
    if (course.value?.examDateDetails?.examTipsDownloadUrl) {
      downloadItem(course.value?.examDateDetails?.examTipsDownloadUrl)
    }
  }

  const removeExamDate = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'ConfirmationModal',
      modalTitle: translateString('examDatePicker.examDateRemoval'),
      modalProps: {
        confirmationText: translateString('examDatePicker.examDateRemovalConfirmationText'),
        okCallback: triggerRemoveDate,
        cancelButtonText: translateString('cancel'),
      },
      cssClass: 'confirmation',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  const triggerRemoveDate = () => {
    UsersApi.cancelExamAppointment(course.value!.id)
      .then(() => {
        CourseModule.setCourseRealExamDate(null)
        UserModule.getUser()
        identifyUserInSegmentWithExamDates(course.value?.shortTitle || course.value!.title, null)
        examAppointmentRemoved(course.value!.id, course.value!.title)
      })
      .finally(() => {
        closeModal()
      })
  }
</script>

<template lang="pug">
.exam-details
  ExamAction.purchased-variant(:action-text='translateString("startNow")', @action='goToExam')
  .exam-date-wrapper
    .exam-date-details
      h4.ketch-font-bold
        span {{ $t('examDatePicker.examDate') }}
        span.ketch-ml-c5(
          :class='{ "ketch-text-[#FF3459]": daysLeftToExam < 10 }',
          v-if='courseExamAppointment && daysLeftToExam > 0'
        ) ({{ daysLeftToExam }} {{ daysLeftToExam === 1 ? $t('day') : $t('days') }})
      .exam-date
        .has-exam-date(v-if='courseExamAppointment')
          h5.ketch-font-bold {{ $t(`courses.examSimulation.${examDateHasPassed ? 'yourExamDateWasOn' : 'yourExamDateIsOn'}`, { examDate: formatDate(courseExamAppointment.date, 'dd.MM.yyyy') }) }}
          h5.remove-date(@click='removeExamDate') {{ $t('examDatePicker.removeDate') }}
        template(v-else)
          .calendar-wrapper
            SVGRenderer(:icon='blankCalendar', :has-hover='false', width='35px', height='35px')
          h5 {{ $t('courses.examSimulation.specifyAnAppointmentText') }}
    KetchUpButton.tertiary(@click.native='handleExamDateActionClick')
      LoadingSpinner(v-if='tipsDownloading')
      h4(v-else) {{ examDateActionText }}
</template>

<style lang="postcss" scoped>
  .exam-details {
    @apply ketch-flex ketch-flex-col ketch-space-y-c20;
    @apply sm:ketch-grid sm:ketch-grid-cols-2 sm:ketch-space-y-0 sm:ketch-border-b sm:ketch-border-border-color;

    .exam-date-wrapper {
      @apply ketch-flex ketch-flex-col ketch-justify-between ketch-bg-content-background-color ketch-rounded-normal ketch-shadow-content-shadow;
      @apply ketch-space-y-c15 ketch-p-c10 xs1:ketch-p-c15 sm:ketch-bg-transparent sm:ketch-rounded-none sm:ketch-p-c20 sm:ketch-shadow-none;

      .exam-date-details {
        @apply ketch-flex ketch-flex-col ketch-space-y-c10;

        .exam-date {
          @apply ketch-p-c10 ketch-rounded-normal ketch-bg-tertiary-color;
          @apply ketch-flex ketch-flex-col ketch-items-center ketch-justify-center ketch-space-y-c10;

          .calendar-wrapper {
            @apply ketch-flex ketch-items-center ketch-justify-center;
            @apply ketch-w-[35px] ketch-h-[35px] ketch-rounded-full ketch-bg-[#E6E6EC];
          }

          h5 {
            @apply ketch-text-center;
          }

          .has-exam-date {
            @apply ketch-flex ketch-flex-col ketch-space-y-c10;

            .remove-date {
              @apply ketch-cursor-pointer ketch-underline ketch-underline-offset-4;
            }
          }
        }
      }

      button {
        @apply ketch-w-full ketch-mt-c20 sm:ketch-mt-0;
      }
    }
  }
</style>
